import _get from 'lodash/get';
import { gdpr } from '@bedrock-rdk/config';
import { statsdMonitoringService } from '@bedrock/fido-sdk';
import { getPlayerInstance } from '@bedrock-rdk/media-engine';
import { deviceInfo } from '@pelo-plugins/device';
import { screenInfo } from '@pelo-plugins/screen';
import { FULL_HD, HD } from '@bedrock-rdk/constants';
import { appVersion, getElapsedTime, get6PlayTime, request, storage } from '@bedrock-rdk/utils';
import { switchToThirdPartyHandlers } from '../../thirdParty/newrelic/newrelic.errorHandler';
import { getGivenConsentVersion, getScreenSize } from './app.selectors';
import { initAnalyticsEngines, bootstrapEngines } from '../analytics/analytics.actions';
import { setAllCookies, areCookiesEmpty } from '../gdprNotice/gdprNotice.utils';
import {
  restoreDeviceData,
  migrateDeviceId,
  USER_RESTORE_PROFILE_UID,
  setUserConsents,
  setGeoInfo,
} from '../user/user.actions';
import { getCurrentPairedUser, clearUserProfile } from '../pairing/pairing.actions';
import {
  getAsyncPlayerChunkFeature,
  getMultiProfilesGateAlwaysDisplayed,
  getPairingFeature,
  getGdprNoticeFeature,
} from '../features/features.selectors';
import { getDeviceId, getPairingStatus, getCookies, getUid } from '../user/user.selectors';
import { errorAction } from '../error/error.actions';
import { ERROR_LEVELS, ERROR_TYPES } from '../error/error.constants';
import { removeSplashScreen, preserveGlobals } from './app.utils';
import { initFidoSDK } from './fido.utils';
import { ERROR_ON_MISSING_CONFIG } from './app.constants';
import { toggleGdprNotice } from '../gdprNotice/gdprNotice.actions';
import { SCREEN_HEIGHT } from '../../modules/tornado/utils/sizes';
import { fetchAppLaunchConfig } from './app.config.actions';

export const INITIALIZE_APP = 'app/INITIALIZE_APP';
export const INITIALIZE_APP_COMPLETED = 'app/INITIALIZE_APP_COMPLETED';
export const INITIALIZE_APP_FAILURE = 'app/INITIALIZE_APP_FAILURE';
export const SET_SERVER_TIME = 'app/SET_SERVER_TIME';
export const SET_CONNECTED = 'app/SET_CONNECTED';
export const SET_SCREEN_SIZE = 'app/SET_SCREEN_SIZE';
export const SET_DEVICE_INFO = 'app/SET_DEVICE_INFO';

export const setScreenSize = screenSize => ({
  type: SET_SCREEN_SIZE,
  size: screenSize,
});

export const getCurrentScreenSize = async () => {
  if (await screenInfo.isGreaterOrEqual(SCREEN_HEIGHT.HD + 1)) {
    return FULL_HD;
  }

  return HD;
};

export const fetchScreenSize = () => async (dispatch, getState) => {
  try {
    const previousScreenSize = getScreenSize(getState());
    const newScreenSize = await getCurrentScreenSize();

    if (newScreenSize !== previousScreenSize) {
      dispatch(setScreenSize(newScreenSize));
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`Could not get device screen info: ${error.message} Are you on browser with a specific platform?`);
    // dispatch(errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.GENERAL, e.toString()));
    dispatch(setScreenSize(HD));
  }
};

export const fetchDeviceInfo = () => async dispatch => {
  const [deviceBrand, deviceModel, deviceVersion] = await Promise.all([
    deviceInfo.getBrand().catch(() => ''),
    deviceInfo.getModel().catch(() => ''),
    deviceInfo.getVersion().catch(() => ''),
  ]);

  dispatch({
    type: SET_DEVICE_INFO,
    deviceBrand,
    deviceModel,
    deviceVersion,
  });
};

export const setServerTime = serverTime => ({
  type: SET_SERVER_TIME,
  serverTime,
});

export const getAssetsBundle = config => async dispatch => {
  const bundleBaseUrl = _get(config, 'assetsBundle.bundleBaseUrl');
  const bundleVersion = _get(config, 'assetsBundle.bundleVersion');

  if (!bundleBaseUrl || !bundleVersion) {
    return;
  }

  const baseUrl = `${bundleBaseUrl}/${bundleVersion}/svg-file`;
  const url = `${baseUrl}/manifest.json`;
  try {
    const manifest = await request(url, { responseType: 'json' });
    config.assetsBundle.manifest = { baseUrl, ...manifest };

    // dispatch(setAssetsBundleConfig(action));
  } catch (error) {
    dispatch(
      errorAction(
        ERROR_LEVELS.SILENT,
        ERROR_TYPES.GENERAL,
        `Failed to retrieve assets bundle manifest: ${error.message}`,
      ),
    );
  }
};

export const authenticateUser = () => async (dispatch, getState) => {
  const state = getState();

  if (!getPairingFeature(state)) {
    return;
  }

  if (!getPairingStatus(state)) {
    try {
      await dispatch(getCurrentPairedUser());
    } catch (error) {
      dispatch(errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.GENERAL, `Authentication failed: ${error.message}`));
    }
  }
};

/**
 * The playback engine lives in its own chunk so it is not parsed before the first paint. With
 * `asyncPlayerChunk` disabled — the default — the chunk is still fetched up front so the engine is
 * in place long before anything can be played, which keeps the risk profile of a bundled engine.
 * Enabling the flag leaves the fetch to the moment playback actually starts.
 */
export const warmUpPlayerChunk = () => (dispatch, getState) => {
  if (getAsyncPlayerChunkFeature(getState())) {
    return;
  }

  getPlayerInstance().catch(error => {
    // NOTE: a failed prefetch must not break the startup sequence — the engine is requested again
    // when playback starts, and that call reports its own failures.
    // eslint-disable-next-line no-console
    console.warn(`[app.actions] Could not pre-load the player chunk: ${error.message}`);
  });
};

export const setFallbackServerTime = () => dispatch => dispatch(setServerTime(Math.floor(Date.now() / 1000)));

export const fetchServerTime = () => async dispatch => {
  try {
    const serverTime = await get6PlayTime();

    return dispatch(setServerTime(serverTime));
  } catch (error) {
    dispatch(errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.GENERAL, error.message));

    return dispatch(setFallbackServerTime());
  }
};

export const initProfiles = () => async (dispatch, getState) => {
  const userShouldSelectProfile = getMultiProfilesGateAlwaysDisplayed(getState());
  if (userShouldSelectProfile) {
    dispatch(clearUserProfile());
  }
};

export const restoreUserProfile = () => (dispatch, getState) => {
  const state = getState();
  const deviceId = getDeviceId(state);
  const userId = getUid(state);

  dispatch(migrateDeviceId(deviceId));

  if (userId) {
    dispatch({ type: USER_RESTORE_PROFILE_UID });
  }
};

export const setUserPrivacy = () => (dispatch, getState) => {
  const state = getState();
  const areEmptyCookies = areCookiesEmpty(getCookies(state));

  if (!getGdprNoticeFeature(state)) {
    // GDPR Notice feature is disabled, assuming user didn't agreed to any tracking
    dispatch(setUserConsents(setAllCookies(false)));
  } else if (areEmptyCookies || gdpr.consentVersion > getGivenConsentVersion(state)) {
    // User didn't set all required tracking preferences, asking for confirmation
    dispatch(toggleGdprNotice());
  } else {
    // User already set tracking preferences, using stored values
    return dispatch(bootstrapEngines());
  }

  // NOTE: just to keep function interface
  return Promise.resolve();
};

export const initializeApp = config => async (dispatch, getState) => {
  if (!config) {
    throw new Error(ERROR_ON_MISSING_CONFIG);
  }

  preserveGlobals();

  let shouldClearLocalStore;
  try {
    const query = new URL(document.location).searchParams;
    shouldClearLocalStore = query.get('cleanup') === 'true';
  } catch (error) {
    shouldClearLocalStore = false;
  }

  if (shouldClearLocalStore) {
    storage.clear();
  }

  // NOTE: it's better to start from less diff with server, should allow to generate proper auth token ASAP and boost app loading a little bit
  dispatch(setFallbackServerTime());

  // Start the startup sequence
  dispatch({
    type: INITIALIZE_APP,
    initialElapsedTime: getElapsedTime(),
  });

  // Set the devices data store in cookies in the state
  dispatch(restoreDeviceData());

  try {
    initFidoSDK(getState);

    await dispatch(fetchAppLaunchConfig(config));

    await Promise.all([
      dispatch(getAssetsBundle(config)),
      dispatch(fetchServerTime()),
      dispatch(fetchScreenSize()),
      dispatch(fetchDeviceInfo()),
      // NOTE: awaited, the IP hash it provides is a part of the initial analytics attributes
      dispatch(setGeoInfo()),
    ]);

    dispatch(warmUpPlayerChunk());

    dispatch(initAnalyticsEngines());

    switchToThirdPartyHandlers();

    await dispatch(initProfiles());

    await dispatch(authenticateUser());

    dispatch(restoreUserProfile());

    statsdMonitoringService.sendAppStart(appVersion);

    dispatch({ type: INITIALIZE_APP_COMPLETED });

    dispatch(setUserPrivacy());
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Can not launch app:', error);
    dispatch(errorAction(ERROR_LEVELS.FATAL, ERROR_TYPES.GENERAL, error.message));
    removeSplashScreen();
  }
};

/**
 * Set connected value, is network online or offline
 * @param {boolean} isConnected
 * @return {{connected: boolean, type: string}}
 */
export const setConnected = isConnected => ({
  type: SET_CONNECTED,
  connected: isConnected,
});
